import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBabyStore } from '@/stores/useBabyStore';
import { api } from '@/lib/api-client';
import { Field } from './Login';
import type { Allergy, DietPreference, FeedStage, FeedType } from '@/types';
import { DIET_OPTIONS } from '@/components/profile/diet-options';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

// ════════════════════════════════════════════════════════════════════
// /onboarding — post-signup baby capture.
//
// Step 1 is the original 4-field form (name, dob, feed_type, conditions).
//
// On Continue, we compute the baby's age. If < 17 weeks (~4 months), we
// submit immediately as before — feed_stage is trivially milk_only and
// allergies/diet are meaningless this young. If >= 17 weeks, we advance
// through 3 mandatory steps before the final write:
//
//   Step 2 — baby allergies (multi-select chips + "None" shortcut)
//   Step 3 — baby feed_stage (radio, pre-selected from age)
//   Step 4 — mother diet preferences (multi-select chips)
//
// On final submit (end of step 4, or end of step 1 for young babies) we:
//   - create the baby with allergies + feed_stage included
//   - update mother profile with diet_preferences
//   - set profile.nutrition_setup_completed_at = NOW()
//   - navigate to /today
//
// Anonymous /start flow is unchanged (it captures only name + dob and
// routes straight to /chat; nutrition prefs are captured later via the
// inline editors on /nutrition).
// ════════════════════════════════════════════════════════════════════

const EXTENDED_FLOW_MIN_WEEKS = 17;

const ALLERGY_OPTIONS: Array<{ v: Allergy; en: string; de: string }> = [
  { v: 'dairy',     en: 'Dairy',     de: 'Milchprodukte' },
  { v: 'eggs',      en: 'Eggs',      de: 'Eier' },
  { v: 'peanuts',   en: 'Peanuts',   de: 'Erdnüsse' },
  { v: 'tree_nuts', en: 'Tree nuts', de: 'Nüsse' },
  { v: 'soy',       en: 'Soy',       de: 'Soja' },
  { v: 'wheat',     en: 'Wheat',     de: 'Weizen' },
  { v: 'fish',      en: 'Fish',      de: 'Fisch' },
  { v: 'shellfish', en: 'Shellfish', de: 'Meeresfrüchte' },
];

const FEED_STAGE_OPTIONS: Array<{ v: FeedStage; en: string; de: string; hint_en: string; hint_de: string }> = [
  { v: 'milk_only',     en: 'Milk only',           de: 'Nur Milch',                hint_en: 'Breast milk or formula, no solids yet',          hint_de: 'Stillen oder Flasche, noch keine feste Nahrung' },
  { v: 'intro_solids',  en: 'Just starting solids', de: 'Beikoststart',             hint_en: 'First tastes of purees or finger foods alongside milk', hint_de: 'Erste Breie oder Fingerfood neben der Milch' },
  { v: 'mixed_diet',    en: 'Mostly solids',       de: 'Hauptsächlich Beikost',    hint_en: 'Eating real meals, milk still part of the day',  hint_de: 'Richtige Mahlzeiten, Milch noch dabei' },
  { v: 'toddler_meals', en: 'Family meals',        de: 'Familienmahlzeiten',       hint_en: 'Eating what the family eats, table foods',       hint_de: 'Isst, was die Familie isst' },
];

function ageWeeksOf(dob: string): number {
  if (!dob) return 0;
  const ms = Date.now() - new Date(dob + 'T00:00:00Z').getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24 * 7)));
}

function defaultFeedStageForAge(ageWeeks: number): FeedStage {
  if (ageWeeks < 26) return 'milk_only';
  if (ageWeeks < 52) return 'intro_solids';
  if (ageWeeks < 78) return 'mixed_diet';
  return 'toddler_meals';
}

export default function Onboarding() {
  const create = useBabyStore((s) => s.create);
  const navigate = useNavigate();
  const de = isGerman();

  // Step 1 (always present) — original 4 fields.
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [feedType, setFeedType] = useState<FeedType>('breastfed');
  const [conditions, setConditions] = useState('');

  // Stepper state. Always starts at 1. Advances to 2-4 only when ageWeeks >= 17.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Phase 3 fields (only collected when extended flow runs).
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [feedStage, setFeedStage] = useState<FeedStage>('milk_only');
  const [diet, setDiet] = useState<DietPreference[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const ageWeeks = useMemo(() => ageWeeksOf(dob), [dob]);
  const isExtendedFlow = ageWeeks >= EXTENDED_FLOW_MIN_WEEKS;

  const finalSubmit = async (
    pickedAllergies: Allergy[],
    pickedFeedStage: FeedStage | null,
    pickedDiet: DietPreference[]
  ) => {
    setLoading(true);
    setError(null);
    try {
      await create({
        name: name.trim(),
        date_of_birth: dob,
        feed_type: feedType,
        conditions: conditions.trim() || undefined,
        allergies: pickedAllergies,
        feed_stage: pickedFeedStage,
      });
      // Only fire the mother update when the extended flow actually ran.
      // Young-baby path leaves mother prefs untouched (still capturable later
      // via Profile → Über dich or /nutrition → Deine Ernährung).
      if (isExtendedFlow) {
        await api.profile.updateMother({
          diet_preferences: pickedDiet,
          nutrition_setup_completed_at: new Date().toISOString(),
        });
      }
      navigate('/today', { replace: true });
    } catch (err) {
      setError((err as Error).message ?? (de ? 'Etwas ist schiefgelaufen.' : 'Something went wrong.'));
      setLoading(false);
    }
  };

  const onStep1Continue = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(de ? 'Bitte den Namen deines Babys eingeben.' : "Please add your baby's name.");
      return;
    }
    if (!dob) {
      setError(de ? 'Bitte das Geburtsdatum eingeben.' : 'Please add a date of birth.');
      return;
    }
    if (!isExtendedFlow) {
      // Young baby — write directly with empty allergies + null feed_stage.
      await finalSubmit([], null, []);
      return;
    }
    // Pre-select feed_stage from age so step 3 is mostly confirmation.
    setFeedStage(defaultFeedStageForAge(ageWeeks));
    setStep(2);
  };

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {step > 1 && (
            <div className="mb-6">
              <div
                className="flex items-center gap-2"
                role="progressbar"
                aria-valuemin={1}
                aria-valuemax={3}
                aria-valuenow={step - 1}
                aria-label={de ? `Schritt ${step - 1} von 3` : `Step ${step - 1} of 3`}
              >
                {[2, 3, 4].map((n) => (
                  <span
                    key={n}
                    className={
                      'h-1.5 rounded-full flex-1 transition-colors ' +
                      (n < step ? 'bg-primary' : n === step ? 'bg-primary/60' : 'bg-primary-100')
                    }
                  />
                ))}
              </div>
              <p className="mt-2 text-[11px] text-slate-muted">
                {de ? `Schritt ${step - 1} von 3 · für ${name}` : `Step ${step - 1} of 3 · for ${name}`}
              </p>
            </div>
          )}

          {/* ─── Step 1 — original form ────────────────────────────── */}
          {step === 1 && (
            <>
              <h1 className="font-display text-3xl font-semibold text-slate text-center">
                {de ? 'Erzähl mir von deinem Baby' : 'Tell me about your baby'}
              </h1>
              <p className="text-slate-muted text-center mt-2 mb-8 text-sm">
                {de
                  ? 'Das bleibt privat in deinem Konto und wird nur zur Personalisierung der Antworten verwendet.'
                  : 'This stays private to your account and is only used to personalize advice.'}
              </p>

              <form onSubmit={onStep1Continue} className="space-y-4">
                <Field
                  label={de ? 'Name des Babys' : "Baby's name"}
                  value={name}
                  onChange={setName}
                  required
                  autoFocus
                  placeholder={de ? 'z. B. Lena' : 'e.g. Maya'}
                />
                <Field
                  label={de ? 'Geburtsdatum' : 'Date of birth'}
                  type="date"
                  value={dob}
                  onChange={setDob}
                  required
                  max={new Date().toISOString().slice(0, 10)}
                />
                <div className="block">
                  <span className="text-sm text-slate font-medium mb-1.5 block">
                    {de ? 'Ernährung' : 'Feeding'}
                  </span>
                  <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={de ? 'Ernährung' : 'Feeding'}>
                    {(['breastfed', 'formula', 'combo'] as FeedType[]).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        role="radio"
                        aria-checked={feedType === opt}
                        onClick={() => setFeedType(opt)}
                        className="choice-segment"
                      >
                        {de
                          ? opt === 'breastfed' ? 'Stillen' : opt === 'formula' ? 'Flasche' : 'Beides'
                          : opt === 'breastfed' ? 'Breastfed' : opt === 'formula' ? 'Formula' : 'Combo'}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="block">
                  <span className="text-sm text-slate font-medium mb-1.5 block">
                    {de ? 'Etwas, das ich wissen sollte?' : 'Anything I should know?'}{' '}
                    <span className="text-slate-muted font-normal">{de ? '(optional)' : '(optional)'}</span>
                  </span>
                  <textarea
                    value={conditions}
                    onChange={(e) => setConditions(e.target.value)}
                    rows={2}
                    placeholder={de ? 'z. B. Frühchen, Reflux, Allergien' : 'e.g. preemie, reflux, allergies'}
                    className="input-field resize-none"
                  />
                </label>

                {error && (
                  <div className="rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} className="btn-primary w-full">
                  {loading ? (de ? 'Wird eingerichtet…' : 'Setting up…') : (de ? 'Weiter' : 'Continue')}
                </button>
              </form>
            </>
          )}

          {/* ─── Step 2 — baby allergies (mandatory engagement, empty=None) ── */}
          {step === 2 && (
            <>
              <h1 className="font-display text-2xl font-semibold text-slate">
                {de ? `Hat ${name} Allergien?` : `Any allergies for ${name}?`}
              </h1>
              <p className="text-sm text-slate-muted mt-1.5 mb-5 leading-relaxed">
                {de
                  ? 'Wähle alle zutreffenden Allergien. Alma blendet diese Lebensmittel aus.'
                  : 'Pick anything that applies. Alma will exclude these from suggestions.'}
              </p>

              <button
                type="button"
                onClick={() => setAllergies([])}
                aria-pressed={allergies.length === 0}
                className="choice-pill mb-3"
              >
                {allergies.length === 0 && <Icon.Check size={16} />}
                {de ? 'Keine Allergien' : 'No allergies'}
              </button>

              <div className="flex flex-wrap gap-2" role="group" aria-label={de ? 'Allergien' : 'Allergies'}>
                {ALLERGY_OPTIONS.map((opt) => {
                  const on = allergies.includes(opt.v);
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setAllergies((cur) =>
                          cur.includes(opt.v) ? cur.filter((a) => a !== opt.v) : [...cur, opt.v]
                        )
                      }
                      className="choice-pill choice-accent"
                    >
                      {de ? opt.de : opt.en}
                    </button>
                  );
                })}
              </div>

              {error && (
                <div className="mt-4 rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(1)} className="btn-ghost text-sm -ml-2">
                  <Icon.ArrowLeft size={16} />
                  {de ? 'Zurück' : 'Back'}
                </button>
                <button type="button" onClick={() => setStep(3)} className="btn-primary">
                  {de ? 'Weiter' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {/* ─── Step 3 — feed_stage (mandatory, pre-selected from age) ── */}
          {step === 3 && (
            <>
              <h1 className="font-display text-2xl font-semibold text-slate">
                {de ? `Wo steht ${name} beim Essen?` : `Where is ${name} at with food?`}
              </h1>
              <p className="text-sm text-slate-muted mt-1.5 mb-5 leading-relaxed">
                {de
                  ? 'Hilft uns, altersgerechte Rezepte vorzuschlagen.'
                  : 'Helps us suggest age-appropriate recipes.'}
              </p>

              <div className="space-y-2" role="radiogroup" aria-label={de ? 'Essphase' : 'Feeding stage'}>
                {FEED_STAGE_OPTIONS.map((opt) => {
                  const on = feedStage === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setFeedStage(opt.v)}
                      className="option-row"
                    >
                      <div className="flex items-center gap-3">
                        <span className="option-dot" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate">{de ? opt.de : opt.en}</div>
                          <div className="text-xs text-slate-muted mt-0.5">{de ? opt.hint_de : opt.hint_en}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-muted pt-2">
                {de
                  ? `Basierend auf ${name}s Alter haben wir eine Auswahl vorausgewählt.`
                  : `We pre-selected a stage based on ${name}'s age.`}
              </p>

              {error && (
                <div className="mt-4 rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(2)} className="btn-ghost text-sm -ml-2">
                  <Icon.ArrowLeft size={16} />
                  {de ? 'Zurück' : 'Back'}
                </button>
                <button type="button" onClick={() => setStep(4)} className="btn-primary">
                  {de ? 'Weiter' : 'Continue'}
                </button>
              </div>
            </>
          )}

          {/* ─── Step 4 — mother diet (mandatory engagement, empty=None) ── */}
          {step === 4 && (
            <>
              <h1 className="font-display text-2xl font-semibold text-slate">
                {de ? 'Was isst du?' : 'What do you eat?'}
              </h1>
              <p className="text-sm text-slate-muted mt-1.5 mb-5 leading-relaxed">
                {de
                  ? 'Damit Alma Mahlzeit-Ideen vorschlagen kann, die zu dir passen.'
                  : 'So Alma can suggest meal ideas that fit you.'}
              </p>

              <div className="flex flex-wrap gap-2" role="group" aria-label={de ? 'Ernährung' : 'Diet'}>
                {DIET_OPTIONS.map((opt) => {
                  const on = diet.includes(opt.v);
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setDiet((cur) =>
                          cur.includes(opt.v) ? cur.filter((d) => d !== opt.v) : [...cur, opt.v]
                        )
                      }
                      className="choice-pill"
                    >
                      {de ? opt.de : opt.en}
                    </button>
                  );
                })}
              </div>

              {error && (
                <div className="mt-4 rounded-lg bg-danger/5 border border-danger/30 px-3 py-2 text-sm text-danger-700">
                  {error}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-3">
                <button type="button" onClick={() => setStep(3)} disabled={loading} className="btn-ghost text-sm -ml-2">
                  <Icon.ArrowLeft size={16} />
                  {de ? 'Zurück' : 'Back'}
                </button>
                <button
                  type="button"
                  onClick={() => finalSubmit(allergies, feedStage, diet)}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? (de ? 'Wird eingerichtet…' : 'Setting up…') : (de ? 'Fertig' : 'Done')}
                </button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
